// @file Dashboard store
// It wraps the dashboard Vuex store into a Pinia store.
import { getVuexStore } from '@@/bits/pinia'
import type { PopoverAnchor } from '@@/library/v4/components/OzPopoverModal.vue'
import type {
  AccountKey,
  Folder,
  FolderId,
  Library,
  LibraryId,
  OwnerOnboardingTask,
  OwnerOnboardingTaskData,
  User,
  UserGroup,
  UserGroupId,
  UserOptions,
  WallCamelCase as Wall,
  WallId,
} from '@@/types'
import type { AccountsMenuItem, DashView, MobilePage, NonCurrentTenantAccountsMenuItem } from '@@/types/dash'
import type { DashState } from '@@/vuexstore/dash'
import type { LibraryIdToUserRoleMap } from '@@/vuexstore/modules/accounts'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useDashboardStore = defineStore('dashboard', () => {
  // State

  // Getters
  const isNativeAccount = computed((): boolean => getVuexStore<DashState>()?.getters.isNativeAccount)
  const isBackpack = computed((): boolean => getVuexStore<DashState>()?.getters.isBackpack)
  const isBriefcase = computed((): boolean => getVuexStore<DashState>()?.getters.isBriefcase)
  const isStudent = computed((): boolean => getVuexStore<DashState>()?.getters.isStudent)
  const currentTenantLogo = computed((): string => getVuexStore<DashState>()?.getters.currentTenantLogo)
  const currentTenantName = computed((): string => getVuexStore<DashState>()?.getters.currentTenantName)
  const currentUser = computed((): User => getVuexStore<DashState>()?.getters.currentUser)
  const currentUserOptions = computed((): UserOptions => getVuexStore<DashState>()?.getters.currentUserOptions)
  const wallViewableAndVisibleLibrariesArray = computed(
    (): Library[] => getVuexStore()?.getters['accounts/wallViewableAndVisibleLibrariesArray'],
  )
  const wallViewableLibrariesArray = computed(
    (): Library[] => getVuexStore()?.getters['accounts/wallViewableLibrariesArray'],
  )
  const wallCreatableLibraries = computed(
    (): Library[] => getVuexStore()?.getters['accounts/wallCreatableLibrariesArray'],
  )

  const userRolesByLibraryId = computed(
    (): LibraryIdToUserRoleMap => getVuexStore()?.getters['accounts/userRolesByLibraryId'],
  )
  const defaultLibrary = computed((): Library | undefined => getVuexStore()?.getters.defaultLibrary)

  const isActiveCollectionCrossLibrary = computed(
    (): boolean => getVuexStore()?.getters['collections/isActiveCollectionCrossLibrary'],
  )
  const currentAccountKey = computed((): AccountKey | null => getVuexStore()?.getters['collections/activeAccountKey'])
  const currentLibrary = computed((): Library | undefined => {
    if (currentAccountKey.value === null) return undefined
    if (currentAccountKey.value.type === 'user') return undefined
    return getVuexStore()?.getters['accounts/librariesById'][currentAccountKey.value.id]
  })

  const activeCollectionWalls = computed((): Wall[] => getVuexStore()?.getters['collections/activeCollectionWalls'])
  const activeUserGroups = computed((): UserGroup[] => getVuexStore()?.getters['collections/activeUserGroups'])
  const currentAccountName = computed((): string => getVuexStore<DashState>()?.getters.currentAccountName)
  const xUserGroupsSection = computed((): boolean => getVuexStore()?.getters.xUserGroupsSection)
  const xSharedCollections = computed((): boolean => getVuexStore()?.getters.xSharedCollections)
  const currentUserAccountKey = computed((): AccountKey => getVuexStore()?.getters.currentUserAccountKey)
  const isActiveGroupId = computed(
    (): ((groupId: UserGroupId) => boolean) => getVuexStore()?.getters['collections/isActiveGroupId'],
  )
  const xGettingStarted = computed((): boolean => getVuexStore()?.getters.xGettingStarted)
  const xGettingStartedRow = computed((): boolean => getVuexStore()?.getters.xGettingStartedRow)
  const currentLibraryDashboardPath = computed((): string => getVuexStore()?.getters.currentLibraryDashboardPath)
  const nonCurrentTenantAccountsMenuList = computed(
    (): NonCurrentTenantAccountsMenuItem[] => getVuexStore()?.getters.nonCurrentTenantAccountsMenuList,
  )
  const accountsMenuList = computed((): AccountsMenuItem[] => getVuexStore()?.getters.accountsMenuList)
  const currentLibraryId = computed((): LibraryId | undefined => getVuexStore()?.getters.currentLibraryId)
  const currentLibraryOwnerOnboardingTasksData = computed(
    (): OwnerOnboardingTaskData[] => getVuexStore()?.getters.currentLibraryOwnerOnboardingTasksData,
  )
  const numOwnerOnboardingTasksCompleted = computed(
    (): number => getVuexStore()?.getters.numOwnerOnboardingTasksCompleted,
  )
  const ownerOnboardingTasksTotal = computed((): number => getVuexStore()?.getters.ownerOnboardingTasksTotal)
  const ownerOnboardingProgressString = computed((): string => getVuexStore()?.getters.ownerOnboardingProgressString)
  const ownerOnboardingTasksAllCompleted = computed(
    (): boolean => getVuexStore()?.getters.ownerOnboardingTasksAllCompleted,
  )
  const currentMobilePage = computed((): MobilePage => getVuexStore()?.getters.currentMobilePage)
  const isCurrentAccountLibrary = computed((): boolean => getVuexStore()?.getters.isCurrentAccountLibrary)
  const dashNameString = computed((): string => getVuexStore()?.getters.dashNameString)
  const dashGreeting = computed((): string => getVuexStore()?.getters.dashGreeting)
  const canViewCrossLibraryGallery = computed((): boolean => getVuexStore()?.getters.canViewCrossLibraryGallery)
  const folderActionsFolder = computed((): Folder | null => getVuexStore()?.getters.folderActionsFolder)
  const folderActionsAnchor = computed((): PopoverAnchor | null => getVuexStore()?.getters.folderActionsAnchor)
  const xFolders = computed((): boolean => getVuexStore()?.getters.xFolders)

  // Actions
  function createWallAndRefetchData(payload: { layout: string; groupBySection?: boolean }): void {
    void getVuexStore<DashState>()?.dispatch('createWallAndRefetchData', payload, { root: true })
  }

  function closeLayoutPicker(): void {
    void getVuexStore<DashState>()?.dispatch('closeLayoutPicker', { root: true })
  }

  function switchView(dashView: DashView): void {
    void getVuexStore<DashState>()?.dispatch('switchView', dashView, { root: true })
  }

  function promptNameForActiveUserNewFolder(): void {
    void getVuexStore<DashState>()?.dispatch('promptNameForActiveUserNewFolder', { root: true })
  }

  function navigateToOwnerOnboardingTask(payload: { taskKey: OwnerOnboardingTask }): void {
    void getVuexStore<DashState>()?.dispatch('navigateToOwnerOnboardingTask', payload, { root: true })
  }

  function showWallActions(payload: { wallId: WallId; popoverAnchor?: PopoverAnchor }): void {
    void getVuexStore<DashState>()?.dispatch('showWallActions', payload, { root: true })
  }

  function showFolderActions(payload: { popoverAnchor: PopoverAnchor; folder: Folder }): void {
    void getVuexStore<DashState>()?.dispatch('showFolderActions', payload, { root: true })
  }

  function confirmDismissOnboarding(): void {
    void getVuexStore<DashState>()?.dispatch('confirmDismissOnboarding', { root: true })
  }

  function promptURLForJoinPadlet(): void {
    void getVuexStore<DashState>()?.dispatch('promptURLForJoinPadlet', { root: true })
  }

  function createWallOrPromptUpgrade(): void {
    void getVuexStore<DashState>()?.dispatch('createWallOrPromptUpgrade', { root: true })
  }
  
  function hideAccountMore(): void {
    void getVuexStore<DashState>()?.dispatch('hideAccountMore', { root: true })
  }

  function closeFolderActions(): void {
    void getVuexStore<DashState>()?.dispatch('closeFolderActions', { root: true })
  }

  function confirmDeleteFolder(payload: { folderId: FolderId }): void {
    void getVuexStore<DashState>()?.dispatch('confirmDeleteFolder', payload, { root: true })
  }

  function promptRenameFolder(payload: { folderId: FolderId }): void {
    void getVuexStore<DashState>()?.dispatch('promptRenameFolder', payload, { root: true })
  }

  return {
    // State

    // Getters
    isNativeAccount,
    isBackpack,
    isBriefcase,
    isStudent,
    currentTenantLogo,
    currentTenantName,
    currentUser,
    currentUserOptions,
    wallViewableAndVisibleLibrariesArray,
    wallViewableLibrariesArray,
    wallCreatableLibraries,
    userRolesByLibraryId,
    defaultLibrary,
    isActiveCollectionCrossLibrary,
    currentAccountKey,
    activeCollectionWalls,
    activeUserGroups,
    isActiveGroupId,
    currentLibrary,
    currentAccountName,
    xUserGroupsSection,
    xSharedCollections,
    currentUserAccountKey,
    xGettingStarted,
    xGettingStartedRow,
    currentLibraryDashboardPath,
    nonCurrentTenantAccountsMenuList,
    currentLibraryId,
    currentLibraryOwnerOnboardingTasksData,
    numOwnerOnboardingTasksCompleted,
    ownerOnboardingTasksTotal,
    ownerOnboardingProgressString,
    ownerOnboardingTasksAllCompleted,
    currentMobilePage,
    isCurrentAccountLibrary,
    dashNameString,
    dashGreeting,
    canViewCrossLibraryGallery,
    accountsMenuList,
    folderActionsAnchor,
    folderActionsFolder,
    xFolders,

    // Actions
    createWallAndRefetchData,
    closeLayoutPicker,
    switchView,
    promptNameForActiveUserNewFolder,
    navigateToOwnerOnboardingTask,
    showWallActions,
    showFolderActions,
    confirmDismissOnboarding,
    promptURLForJoinPadlet,
    createWallOrPromptUpgrade,
    hideAccountMore,
    closeFolderActions,
    confirmDeleteFolder,
    promptRenameFolder,
  }
})
